/** Device presence write model: heartbeat, rotation, reaping. */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { createPersonOwnedDocument } from '../project/creation'
import type { WorldDoc } from '../world/types'
import type { HarnessPresence, PresenceDoc, ProjectMount } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any
/** Create an empty device-presence doc (RFC 0261 slice 2). Synchronous. Kept
 *  separate from the World config so heartbeat churn never touches the directory. */
export function createPresence(repo: Repo): DocHandle<PresenceDoc> {
  const handle = repo.create<PresenceDoc>()
  handle.change((d) => {
    d.devices = {}
  })
  return handle
}

/**
 * Ensure a World has a presence doc, creating and linking one on first use.
 * Returns the presence doc id. Idempotent — a World that already points at a
 * presence doc keeps it.
 */
/**
 * Rotate the presence doc when the current one is older than this. Presence
 * accumulates a heartbeat write every ~10s per device FOREVER (Automerge never
 * sheds history), and hydrating a doc bloated past the wasm's tolerance PANICS
 * the node subduction wasm — the trap→deaf-connection→"unavailable" failure
 * diagnosed live. Its content is worthless beyond the ~30s liveness window by
 * design, so the doc itself is disposable: rotation to a fresh doc is the cure.
 * 6h ≈ a few thousand changes per device, comfortably small.
 */
export const PRESENCE_ROTATION_INTERVAL_MS = 6 * 60 * 60 * 1000
/**
 * Ensure a World has a FRESH-ENOUGH presence doc, creating or rotating one as
 * needed. Returns the presence doc id. Long-lived hosts call this per heartbeat
 * (cheap: one small-World read), so a host that outlives the rotation interval
 * rotates in place and every reader follows the pointer.
 *
 * DELIBERATELY never opens the old presence doc — not to count changes, not to
 * copy content. Deciding rotation by hydrating the possibly-bloated doc would
 * trigger the exact wasm trap rotation exists to prevent; the decision reads
 * only the World's `presenceRotatedAt` stamp. The old doc is simply abandoned
 * (acceptable orphan pre-launch; readers move with the pointer, and a beating
 * device re-appears in the fresh doc on its next heartbeat).
 *
 * Concurrent rotation from two hosts LWW-converges on one pointer; the loser's
 * doc is one more orphan and its devices re-register on the next beat.
 */
export async function ensureWorldPresence(
  repo: Repo,
  worldId: string,
  opts: { now?: number; rotationIntervalMs?: number } = {},
): Promise<string> {
  const now = opts.now ?? Date.now()
  const interval = opts.rotationIntervalMs ?? PRESENCE_ROTATION_INTERVAL_MS
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  const doc = world.doc()
  const existing = doc?.presenceDocId
  const rotatedAt = doc?.presenceRotatedAt

  const fresh =
    typeof existing === 'string' &&
    existing.length > 0 &&
    typeof rotatedAt === 'number' &&
    now - rotatedAt <= interval
  if (fresh) return existing as string

  // Absent, stale, or unstamped (a pre-rotation World): link a fresh doc. An
  // unstamped-but-linked presence doc rotates immediately — its age is unknown
  // and it may already be bloated, so treating it as stale is the safe read.
  // A sharing repo creates only owned documents: the person's group owns
  // presence, as it owns the World that points at it (RFC 0345).
  const presence = await createPersonOwnedDocument<PresenceDoc>(repo, {
    devices: {},
  } as PresenceDoc)
  const presenceId = presence.documentId as string
  world.change((d: Draft) => {
    d.presenceDocId = presenceId
    d.presenceRotatedAt = now
  })
  return presenceId
}
/**
 * A key-order-independent rendering, so a snapshot read back from Automerge
 * (which enumerates map keys sorted) compares equal to the plain object it was
 * written from.
 */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v))
      return Object.fromEntries(
        Object.keys(v as Record<string, unknown>)
          .sort()
          .map((k) => [k, (v as Record<string, unknown>)[k]]),
      )
    // A list of strings (a harness's models) is a set here: the order a
    // probe returns it in is not a change worth rewriting the record for.
    if (Array.isArray(v) && v.every((x) => typeof x === 'string'))
      return [...v].sort()
    return v
  })
}
/**
 * Stamp this device's heartbeat (RFC 0261 slice 2). Upserts `lastSeen = now`
 * plus its label/kind/model. A caller beats on an interval; a device is present
 * only while its beats stay inside the liveness window, so stopping is all it
 * takes to go absent (no explicit offline write).
 *
 * THE SNAPSHOTS ARE WRITTEN ONLY WHEN THEY CHANGE. `harnesses` and `mounts`
 * are replaced wholesale, and a wholesale replacement of a nested object is an
 * op per key, per element, per string, deleted and re-inserted: with Cursor
 * reporting 223 models, one beat cost about five thousand ops. Automerge keeps
 * every op. At a beat every ten seconds a presence doc reached twelve million
 * ops inside its six-hour rotation window, three rotated docs stayed loaded,
 * and the app's wasm memory sat at three gigabytes on an eight-gigabyte laptop,
 * which was the whole machine swapping. A beat whose snapshot has not changed
 * now writes one op, `lastSeen`.
 */
export async function heartbeatDevice(
  repo: Repo,
  presenceId: string,
  deviceId: string,
  opts: {
    name: string
    now: number
    kind?: string
    defaultModel?: string
    /** The harnesses this device can drive (RFC 0269). */
    harnesses?: HarnessPresence[]
    /** The projects this device serves, and where each lives on it (RFC 0290
     *  §3b). Replaced wholesale, like `harnesses`. */
    mounts?: ProjectMount[]
  },
): Promise<void> {
  const handle = await repo.find<PresenceDoc>(presenceId as AnyDocumentId)
  // Compared OUTSIDE the change, against the committed doc, so an unchanged
  // snapshot costs a read and not a write.
  const before = handle.doc()?.devices?.[deviceId]
  const harnessesChanged =
    opts.harnesses !== undefined &&
    stableJson(before?.harnesses ?? null) !== stableJson(opts.harnesses)
  const mountsChanged =
    opts.mounts !== undefined &&
    stableJson(before?.mounts ?? null) !== stableJson(opts.mounts)
  handle.change((d: Draft) => {
    if (!d.devices) d.devices = {}
    const existing = d.devices[deviceId]
    if (existing) {
      // Field-wise in place; re-assigning an existing doc object throws in Automerge.
      if (existing.name !== opts.name) existing.name = opts.name
      existing.lastSeen = opts.now
      if (opts.kind !== undefined && existing.kind !== opts.kind)
        existing.kind = opts.kind
      if (
        opts.defaultModel !== undefined &&
        existing.defaultModel !== opts.defaultModel
      )
        existing.defaultModel = opts.defaultModel
      if (harnessesChanged) {
        // Replaced wholesale: this is a snapshot of what the device sees right
        // now, and a harness that was uninstalled must disappear rather than
        // linger as a stale entry the picker would still offer.
        existing.harnesses = opts.harnesses as never
      }
      if (mountsChanged) {
        // Same rule, same reason: a project this daemon no longer serves must
        // stop being offered, and merging would leave it listed forever.
        existing.mounts = opts.mounts as never
      }
      return
    }
    d.devices[deviceId] = {
      name: opts.name,
      lastSeen: opts.now,
      ...(opts.kind !== undefined ? { kind: opts.kind } : {}),
      ...(opts.harnesses !== undefined ? { harnesses: opts.harnesses } : {}),
      ...(opts.mounts !== undefined ? { mounts: opts.mounts } : {}),
      ...(opts.defaultModel !== undefined
        ? { defaultModel: opts.defaultModel }
        : {}),
    }
  })
}
/**
 * Prune devices unseen for longer than `ttlMs`. Cleanup to bound the doc, NOT
 * correctness — a reader already ignores anything outside the liveness window,
 * so a dead entry that lingers is invisible until reaped. Returns the reaped ids.
 */
export async function reapDevices(
  repo: Repo,
  presenceId: string,
  now: number,
  ttlMs: number,
): Promise<string[]> {
  const handle = await repo.find<PresenceDoc>(presenceId as AnyDocumentId)
  const reaped: string[] = []
  handle.change((d: Draft) => {
    for (const [id, dev] of Object.entries(
      (d.devices ?? {}) as Record<string, { lastSeen?: number }>,
    )) {
      if (typeof dev?.lastSeen === 'number' && now - dev.lastSeen > ttlMs) {
        delete d.devices[id]
        reaped.push(id)
      }
    }
  })
  return reaped
}
